/**
 * /slides/:id 우측 패널 — 선택된 페이지의 layout / blocks 인라인 편집.
 *
 * 동작:
 *  - layout 드롭다운 — 즉시 저장 (debounce 안 함, 명시 동작)
 *  - 각 블록 type 별 입력 컴포넌트 (텍스트/select)
 *  - 텍스트 입력은 디바운스 (350ms) 후 자동 저장
 *  - 블록 추가/삭제/위↑아래↓ 이동
 *  - 모든 변경 후 [POST /api/slides/:id/pages/:pid/update] 한 번에 content 통째로 patch
 *
 * tsx 모드 페이지는 "TSX 편집기" 안내만 (별도 PR 에서 본격 sandbox 추가 예정).
 */
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { api } from '@/lib/api';
import type {
  SlideAsset,
  SlideBlock,
  SlideLayout,
  SlidePage,
  SlidePageBlocksContentSchema,
} from '@innermate/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, History, ImageIcon, Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { z } from 'zod';
import { PageVersionHistory } from './PageVersionHistory';

type BlocksContent = z.infer<typeof SlidePageBlocksContentSchema>;
type BlockType = SlideBlock['type'];

const LAYOUTS: SlideLayout[] = [
  'hero',
  'title-body',
  'two-column',
  'image-left',
  'image-right',
  'image-full',
  'bullets',
  'quote',
  'kpi-row',
  'blank',
];

const BLOCK_TYPE_LABEL: Record<BlockType, string> = {
  heading: '제목',
  body: '본문',
  bullets: '불릿',
  quote: '인용',
  image: '이미지',
  kpi: 'KPI',
  spacer: '여백',
};

function makeNewBlock(type: BlockType, assetIdHint?: string): SlideBlock {
  switch (type) {
    case 'heading':
      return { type: 'heading', level: 'section', text: '제목', align: 'left' };
    case 'body':
      return { type: 'body', text: '본문 내용', size: 'md', align: 'left' };
    case 'bullets':
      return { type: 'bullets', items: ['항목 1', '항목 2'], style: 'dot' };
    case 'quote':
      return { type: 'quote', text: '인용문' };
    case 'image':
      return { type: 'image', assetId: assetIdHint ?? '', fit: 'cover' };
    case 'kpi':
      return { type: 'kpi', label: '지표', value: '0' };
    case 'spacer':
      return { type: 'spacer', size: 'md' };
  }
}

interface Props {
  slideId: string;
  page: SlidePage;
  assets: SlideAsset[];
  /** 캔버스에서 클릭한 block 의 인덱스. 인스펙터 안에서 해당 블록 폼이 강조 + scroll. */
  selectedBlockIndex?: number | null;
}

export function PageInspector({ slideId, page, assets, selectedBlockIndex }: Props) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const inner =
    page.content.mode === 'tsx' ? (
      <TsxInspector
        key={page.id}
        slideId={slideId}
        pageId={page.id}
        initialSource={page.content.tsxSource}
        assets={assets}
      />
    ) : (
      <BlocksInspector
        key={page.id}
        slideId={slideId}
        pageId={page.id}
        initialContent={page.content}
        assets={assets}
        selectedBlockIndex={selectedBlockIndex ?? null}
      />
    );

  return (
    <div>
      <div className="flex justify-end -mt-1 mb-2">
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="inline-flex items-center gap-1 text-[11px] text-foreground-muted hover:text-foreground"
          title="이 페이지의 변경 이력 보기"
        >
          <History className="size-3" />
          히스토리
        </button>
      </div>
      {inner}
      <PageVersionHistory
        slideId={slideId}
        pageId={page.id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}

const DEFAULT_BLOCKS_FOR_TSX_FALLBACK: BlocksContent = {
  mode: 'blocks',
  layout: 'title-body',
  blocks: [
    { type: 'heading', level: 'section', text: '새 페이지', align: 'left' },
    { type: 'body', text: '내용을 입력하세요.', size: 'md', align: 'left' },
  ],
};

const DEFAULT_TSX_TEMPLATE = `export default function Page() {
  return (
    <div style={{
      width:'100%', height:'100%',
      padding:'120px 140px',
      background:'var(--osd-bg)', color:'var(--osd-text)',
      fontFamily:'var(--osd-font-body)',
      display:'flex', flexDirection:'column', justifyContent:'center', gap:48,
    }}>
      <h1 style={{
        margin:0, fontFamily:'var(--osd-font-display)',
        fontSize:'var(--osd-size-hero)', lineHeight:1.1, wordBreak:'keep-all',
      }}>
        제목
      </h1>
      <p style={{
        margin:0, fontSize:'var(--osd-size-body)',
        lineHeight:1.45, wordBreak:'keep-all',
      }}>
        본문 한 단락. var(--osd-accent) 로 강조 가능.
      </p>
    </div>
  );
}
`;

function TsxInspector({
  slideId,
  pageId,
  initialSource,
  assets,
}: {
  slideId: string;
  pageId: string;
  initialSource: string;
  assets: SlideAsset[];
}) {
  const qc = useQueryClient();
  const [source, setSource] = useState(initialSource || DEFAULT_TSX_TEMPLATE);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    setSource(initialSource || DEFAULT_TSX_TEMPLATE);
  }, [initialSource]);

  const updateMut = useMutation({
    mutationFn: (next: string) =>
      api.slideUpdatePage(slideId, pageId, {
        content: { mode: 'tsx', tsxSource: next },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['slide', slideId] }),
  });
  useDebouncedCallback(source, 600, (next) => {
    if (next !== initialSource) updateMut.mutate(next);
  });

  const switchToBlocks = () => {
    if (
      !confirm(
        'TSX 모드에서 blocks 모드로 전환하면 현재 코드는 삭제되고 빈 페이지로 시작합니다. 계속할까요?',
      )
    ) {
      return;
    }
    api
      .slideUpdatePage(slideId, pageId, { content: DEFAULT_BLOCKS_FOR_TSX_FALLBACK })
      .then(() => qc.invalidateQueries({ queryKey: ['slide', slideId] }));
  };

  /** 현재 textarea 의 selection 위치에 텍스트 splice + 커서를 삽입 끝으로 이동. */
  const insertAtCursor = (text: string) => {
    const ta = textareaRef.current;
    if (!ta) {
      // ref 없으면 끝에 append.
      setSource((s) => s + text);
      return;
    }
    const start = ta.selectionStart ?? source.length;
    const end = ta.selectionEnd ?? source.length;
    const next = source.slice(0, start) + text + source.slice(end);
    setSource(next);
    // React state 갱신 후 다음 paint 에 setSelectionRange.
    requestAnimationFrame(() => {
      const cursor = start + text.length;
      ta.focus();
      ta.setSelectionRange(cursor, cursor);
    });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] uppercase tracking-wide text-foreground-muted">
        <span>TSX 코드</span>
        <span>
          {updateMut.isPending ? '저장 중…' : updateMut.isError ? '저장 실패' : '자동 저장됨'}
        </span>
      </div>
      <textarea
        ref={textareaRef}
        value={source}
        onChange={(e) => setSource(e.target.value)}
        rows={18}
        className="w-full text-[11px] font-mono leading-relaxed rounded-md border border-border bg-surface-muted/30 p-2 focus:outline-none focus:ring-1 focus:ring-accent"
        spellCheck={false}
      />
      <div className="text-[10px] text-foreground-muted leading-relaxed">
        export default function 컴포넌트 1개. import 금지.
        var(--osd-bg/text/accent/font-display/font-body/size-hero/size-body/radius) 디자인 토큰 사용
        가능. 자산은 useAsset(assetId) 글로벌 헬퍼.
      </div>

      {/* 자산 picker — 클릭 시 커서 위치에 useAsset('uuid') 자동 삽입 */}
      <div className="pt-2 border-t border-border space-y-1.5">
        <div className="text-[11px] uppercase tracking-wide text-foreground-muted">자산 삽입</div>
        {assets.length === 0 ? (
          <p className="text-[10px] text-foreground-muted leading-relaxed">
            아직 자산이 없어요. 우측 하단 패널에서 이미지를 먼저 업로드한 뒤 카드를 클릭하면
            <code className="mx-1 px-1 py-0.5 rounded bg-surface-muted/50 text-[10px]">
              useAsset('uuid')
            </code>
            가 코드 커서 위치에 자동 삽입됩니다.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {assets.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => insertAtCursor(`useAsset('${a.id}')`)}
                title={`${a.filename} → useAsset('${a.id}') 삽입`}
                className="relative aspect-video rounded border border-border hover:border-accent overflow-hidden"
              >
                <img src={a.url} alt={a.filename} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={switchToBlocks}
        className="text-[10px] text-foreground-muted hover:text-destructive underline underline-offset-2"
      >
        blocks 모드로 되돌리기 (코드 삭제)
      </button>
    </div>
  );
}

function BlocksInspector({
  slideId,
  pageId,
  initialContent,
  assets,
  selectedBlockIndex,
}: {
  slideId: string;
  pageId: string;
  initialContent: BlocksContent;
  assets: SlideAsset[];
  selectedBlockIndex: number | null;
}) {
  const qc = useQueryClient();
  // 외부에서 갱신된 content (다른 turn 의 모델 변경 등) 를 동기화.
  const [content, setContent] = useState<BlocksContent>(initialContent);
  useEffect(() => {
    setContent(initialContent);
  }, [initialContent]);

  // 선택된 block 의 카드 DOM 으로 scrollIntoView.
  const activeCardRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (selectedBlockIndex === null) return;
    const node = activeCardRef.current;
    if (node) node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedBlockIndex]);

  const updateMut = useMutation({
    mutationFn: (next: BlocksContent) => api.slideUpdatePage(slideId, pageId, { content: next }),
    onSuccess: () => {
      // 캔버스 즉시 invalidate — 사용자가 우측에서 친 게 좌측 캔버스에 반영.
      qc.invalidateQueries({ queryKey: ['slide', slideId] });
    },
  });

  const flushFn = (next: BlocksContent) => {
    updateMut.mutate(next);
  };
  const { flush: flushDebounced } = useDebouncedCallback(content, 350, flushFn);

  const setLayout = (layout: SlideLayout) => {
    const next: BlocksContent = { ...content, layout };
    setContent(next);
    updateMut.mutate(next); // layout 변경은 즉시.
  };

  const setBlock = (idx: number, b: SlideBlock) => {
    const next: BlocksContent = {
      ...content,
      blocks: content.blocks.map((x, i) => (i === idx ? b : x)),
    };
    setContent(next);
  };

  const moveBlock = (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= content.blocks.length) return;
    const next = { ...content, blocks: [...content.blocks] };
    [next.blocks[idx], next.blocks[target]] = [
      next.blocks[target] as SlideBlock,
      next.blocks[idx] as SlideBlock,
    ];
    setContent(next);
    updateMut.mutate(next);
  };

  const deleteBlock = (idx: number) => {
    const next: BlocksContent = {
      ...content,
      blocks: content.blocks.filter((_, i) => i !== idx),
    };
    setContent(next);
    updateMut.mutate(next);
  };

  const addBlock = (type: BlockType) => {
    const next: BlocksContent = {
      ...content,
      blocks: [...content.blocks, makeNewBlock(type, assets[0]?.id)],
    };
    setContent(next);
    updateMut.mutate(next);
  };

  return (
    <div className="space-y-3">
      {/* layout */}
      <label className="block">
        <div className="text-[11px] uppercase tracking-wide text-foreground-muted mb-1">
          레이아웃
        </div>
        <select
          value={content.layout}
          onChange={(e) => setLayout(e.target.value as SlideLayout)}
          className="w-full text-xs rounded-md border border-border bg-surface-muted/30 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent"
        >
          {LAYOUTS.map((l) => (
            <option key={l} value={l}>
              {l}
            </option>
          ))}
        </select>
      </label>

      {/* blocks */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-wide text-foreground-muted">
            블록 ({content.blocks.length})
          </div>
          <span className="text-[10px] text-foreground-muted">
            {updateMut.isPending ? '저장 중…' : updateMut.isError ? '저장 실패' : '자동 저장됨'}
          </span>
        </div>
        <div className="space-y-2">
          {content.blocks.map((b, i) => {
            const isActive = selectedBlockIndex === i;
            return (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: blocks 는 안정 id 가 없고 같은 페이지 안에서만 렌더 — 인덱스 키로 충분.
                key={i}
                ref={isActive ? activeCardRef : null}
                data-block-card-index={i}
                className={
                  isActive
                    ? 'rounded-md ring-2 ring-accent ring-offset-2 ring-offset-background transition-shadow'
                    : ''
                }
              >
                <BlockCard
                  block={b}
                  index={i}
                  total={content.blocks.length}
                  assets={assets}
                  onChange={(next) => {
                    setBlock(i, next);
                    // 텍스트 변경은 디바운스, 다른 컨트롤은 즉시.
                    if (next.type === b.type && hasOnlyTextChange(b, next)) {
                      // useDebouncedCallback 이 content 를 watch 하니 setContent 만 하면 자동 디바운스.
                    } else {
                      updateMut.mutate({
                        ...content,
                        blocks: content.blocks.map((x, j) => (j === i ? next : x)),
                      });
                    }
                  }}
                  onMoveUp={() => moveBlock(i, -1)}
                  onMoveDown={() => moveBlock(i, 1)}
                  onDelete={() => deleteBlock(i)}
                />
              </div>
            );
          })}
        </div>

        <AddBlockMenu onAdd={addBlock} />
      </div>
      <div className="text-[10px] text-foreground-muted leading-relaxed pt-1">
        텍스트는 입력 후 약 0.4초 뒤 자동 저장. 좌측 캔버스에 즉시 반영.
      </div>
      <button type="button" onClick={() => flushDebounced()} className="hidden" aria-hidden />

      <div className="pt-2 border-t border-border">
        <button
          type="button"
          onClick={() => {
            if (
              confirm(
                'TSX 모드로 전환하면 현재 블록 구성은 사라지고 빈 React 컴포넌트로 시작합니다. 계속할까요?',
              )
            ) {
              api
                .slideUpdatePage(slideId, pageId, {
                  content: { mode: 'tsx', tsxSource: DEFAULT_TSX_TEMPLATE },
                })
                .then(() => qc.invalidateQueries({ queryKey: ['slide', slideId] }));
            }
          }}
          className="text-[10px] text-foreground-muted hover:text-foreground underline underline-offset-2"
        >
          TSX 모드로 전환 (자유 React 코드)
        </button>
      </div>
    </div>
  );
}

/**
 * 같은 type 인 두 block 사이의 변경이 텍스트 필드만 바뀐 케이스인지 — 디바운스 적용 여부 판단.
 * select/enum 값(level/size/align/style/fit)은 즉시 저장이 자연스럽고,
 * text/items/label/value/delta/caption/attribution 같은 자유 텍스트는 디바운스.
 */
function hasOnlyTextChange(prev: SlideBlock, next: SlideBlock): boolean {
  if (prev.type !== next.type) return false;
  switch (prev.type) {
    case 'heading':
    case 'body':
    case 'quote':
      return (
        next.type === prev.type &&
        // 모든 enum 동일하고 text 만 다르면 true
        ((prev.type === 'heading' &&
          next.type === 'heading' &&
          prev.level === next.level &&
          prev.align === next.align) ||
          (prev.type === 'body' &&
            next.type === 'body' &&
            prev.size === next.size &&
            prev.align === next.align) ||
          (prev.type === 'quote' && next.type === 'quote'))
      );
    case 'bullets':
      return (
        next.type === 'bullets' &&
        prev.style === next.style &&
        prev.items.length === next.items.length
      );
    case 'kpi':
      return next.type === 'kpi';
    case 'image':
      return next.type === 'image' && prev.assetId === next.assetId && prev.fit === next.fit;
    case 'spacer':
      return false;
    default:
      return false;
  }
}

function BlockCard({
  block,
  index,
  total,
  assets,
  onChange,
  onMoveUp,
  onMoveDown,
  onDelete,
}: {
  block: SlideBlock;
  index: number;
  total: number;
  assets: SlideAsset[];
  onChange: (b: SlideBlock) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="rounded-md border border-border bg-surface-muted/20 p-2">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-medium text-foreground-muted">
          {index + 1}. {BLOCK_TYPE_LABEL[block.type]}
        </span>
        <div className="flex items-center -mr-1">
          <IconBtn
            label="위로"
            onClick={onMoveUp}
            disabled={index === 0}
            icon={<ChevronUp className="size-3.5" />}
          />
          <IconBtn
            label="아래로"
            onClick={onMoveDown}
            disabled={index === total - 1}
            icon={<ChevronDown className="size-3.5" />}
          />
          <IconBtn label="삭제" onClick={onDelete} danger icon={<Trash2 className="size-3.5" />} />
        </div>
      </div>
      <BlockEditor block={block} assets={assets} onChange={onChange} />
    </div>
  );
}

function IconBtn({
  label,
  onClick,
  icon,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  icon: React.ReactNode;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`p-1 rounded hover:bg-surface-muted/60 disabled:opacity-30 disabled:hover:bg-transparent ${
        danger ? 'text-foreground-muted hover:text-destructive' : 'text-foreground-muted'
      }`}
    >
      {icon}
    </button>
  );
}

function BlockEditor({
  block,
  assets,
  onChange,
}: {
  block: SlideBlock;
  assets: SlideAsset[];
  onChange: (b: SlideBlock) => void;
}) {
  switch (block.type) {
    case 'heading':
      return (
        <div className="space-y-1.5">
          <textarea
            value={block.text}
            rows={2}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            className={textareaClass()}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <Select
              value={block.level ?? 'section'}
              onChange={(v) => onChange({ ...block, level: v as 'hero' | 'section' | 'sub' })}
              options={['hero', 'section', 'sub']}
              label="크기"
            />
            <Select
              value={block.align ?? 'left'}
              onChange={(v) => onChange({ ...block, align: v as 'left' | 'center' | 'right' })}
              options={['left', 'center', 'right']}
              label="정렬"
            />
          </div>
        </div>
      );
    case 'body':
      return (
        <div className="space-y-1.5">
          <textarea
            value={block.text}
            rows={3}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            className={textareaClass()}
          />
          <div className="grid grid-cols-2 gap-1.5">
            <Select
              value={block.size ?? 'md'}
              onChange={(v) => onChange({ ...block, size: v as 'lg' | 'md' | 'sm' })}
              options={['lg', 'md', 'sm']}
              label="크기"
            />
            <Select
              value={block.align ?? 'left'}
              onChange={(v) => onChange({ ...block, align: v as 'left' | 'center' | 'right' })}
              options={['left', 'center', 'right']}
              label="정렬"
            />
          </div>
        </div>
      );
    case 'bullets':
      return (
        <div className="space-y-1.5">
          {block.items.map((it, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: bullets items 는 안정 id 없음 — 같은 블록 안에서만 렌더되므로 index 키로 충분.
            <div key={i} className="flex items-start gap-1">
              <span className="text-[11px] text-foreground-muted mt-1.5 w-3 text-right">
                {i + 1}
              </span>
              <textarea
                value={it}
                rows={1}
                onChange={(e) => {
                  const items = block.items.map((x, j) => (j === i ? e.target.value : x));
                  onChange({ ...block, items });
                }}
                className={`${textareaClass()} flex-1`}
              />
              <button
                type="button"
                onClick={() => {
                  if (block.items.length <= 1) return;
                  const items = block.items.filter((_, j) => j !== i);
                  onChange({ ...block, items });
                }}
                disabled={block.items.length <= 1}
                aria-label="삭제"
                className="p-1 text-foreground-muted hover:text-destructive disabled:opacity-30"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => {
                if (block.items.length >= 7) return;
                onChange({ ...block, items: [...block.items, '새 항목'] });
              }}
              disabled={block.items.length >= 7}
              className="text-[11px] text-foreground-muted hover:text-foreground inline-flex items-center gap-1 disabled:opacity-30"
            >
              <Plus className="size-3" /> 항목 추가
            </button>
            <span className="text-[10px] text-foreground-muted ml-auto">
              ({block.items.length}/7)
            </span>
          </div>
          <Select
            value={block.style ?? 'dot'}
            onChange={(v) => onChange({ ...block, style: v as 'dot' | 'dash' | 'numbered' })}
            options={['dot', 'dash', 'numbered']}
            label="스타일"
          />
        </div>
      );
    case 'quote':
      return (
        <div className="space-y-1.5">
          <textarea
            value={block.text}
            rows={2}
            onChange={(e) => onChange({ ...block, text: e.target.value })}
            className={textareaClass()}
            placeholder="인용문"
          />
          <input
            type="text"
            value={block.attribution ?? ''}
            onChange={(e) => onChange({ ...block, attribution: e.target.value || undefined })}
            className={inputClass()}
            placeholder="출처 (선택)"
          />
        </div>
      );
    case 'image':
      return (
        <div className="space-y-1.5">
          {assets.length === 0 ? (
            <p className="text-[10px] text-foreground-muted leading-relaxed">
              아직 자산이 없어요. 우측 패널 하단에서 이미지를 먼저 업로드하세요.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-1">
              {assets.map((a) => (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => onChange({ ...block, assetId: a.id })}
                  className={`relative aspect-video rounded border overflow-hidden ${
                    block.assetId === a.id ? 'border-accent ring-1 ring-accent' : 'border-border'
                  }`}
                  title={a.filename}
                >
                  <img src={a.url} alt={a.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {!block.assetId && (
            <div className="text-[10px] text-foreground-muted flex items-center gap-1">
              <ImageIcon className="size-3" /> 자산을 선택하세요
            </div>
          )}
          <Select
            value={block.fit ?? 'cover'}
            onChange={(v) => onChange({ ...block, fit: v as 'cover' | 'contain' })}
            options={['cover', 'contain']}
            label="채움"
          />
          <input
            type="text"
            value={block.caption ?? ''}
            onChange={(e) => onChange({ ...block, caption: e.target.value || undefined })}
            className={inputClass()}
            placeholder="캡션 (선택)"
          />
        </div>
      );
    case 'kpi':
      return (
        <div className="space-y-1.5">
          <input
            type="text"
            value={block.label}
            onChange={(e) => onChange({ ...block, label: e.target.value })}
            className={inputClass()}
            placeholder="라벨"
          />
          <input
            type="text"
            value={block.value}
            onChange={(e) => onChange({ ...block, value: e.target.value })}
            className={`${inputClass()} font-mono`}
            placeholder="값"
          />
          <input
            type="text"
            value={block.delta ?? ''}
            onChange={(e) => onChange({ ...block, delta: e.target.value || undefined })}
            className={inputClass()}
            placeholder="변동치 (선택)"
          />
        </div>
      );
    case 'spacer':
      return (
        <Select
          value={block.size ?? 'md'}
          onChange={(v) => onChange({ ...block, size: v as 'sm' | 'md' | 'lg' })}
          options={['sm', 'md', 'lg']}
          label="크기"
        />
      );
  }
}

function Select({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  label: string;
}) {
  return (
    <label className="block">
      <span className="text-[10px] text-foreground-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-xs rounded-md border border-border bg-surface-muted/30 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function AddBlockMenu({ onAdd }: { onAdd: (t: BlockType) => void }) {
  const [open, setOpen] = useState(false);
  const types: BlockType[] = ['heading', 'body', 'bullets', 'quote', 'image', 'kpi', 'spacer'];
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-xs rounded-md border border-dashed border-border py-1.5 text-foreground-muted hover:text-foreground hover:border-foreground-muted transition-colors inline-flex items-center justify-center gap-1"
      >
        <Plus className="size-3" /> 블록 추가
      </button>
      {open && (
        <div className="mt-1 grid grid-cols-2 gap-1">
          {types.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => {
                onAdd(t);
                setOpen(false);
              }}
              className="text-[11px] py-1 rounded border border-border hover:bg-surface-muted/60"
            >
              {BLOCK_TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function textareaClass() {
  return 'w-full text-xs rounded-md border border-border bg-surface-muted/30 px-2 py-1.5 leading-relaxed resize-none focus:outline-none focus:ring-1 focus:ring-accent';
}
function inputClass() {
  return 'w-full text-xs rounded-md border border-border bg-surface-muted/30 px-2 py-1 focus:outline-none focus:ring-1 focus:ring-accent';
}
